import React, { FC, useEffect, useState } from 'react'
import { SearchForm, SelectBox } from 'components/Form'
import { Spacer } from 'components/Utility'
import { StockCardList } from 'components/Card'
import { useDispatch, useSelector } from 'react-redux'
import { getCategories } from 'reducks/category/selectors'
import { State } from 'index'
import { fetchEnabledCategories } from 'reducks/category/operations'
import { getBreads } from 'reducks/bread/selectors'
import { fetchBreads } from 'reducks/bread/operations'
import { BreadType } from 'types/bread'
import { useCategoryOptions } from 'hooks/category'
import { getStoreRef } from 'reducks/auth/selectors'
import { StockType } from 'types/stock'
import { useAdjustStocks, useHandleKeyword, useInitBreadList, useSearchStocks, useSelectCategory } from 'hooks/stock'
import { OrderType } from 'types/checkout'
import { getOrders } from 'reducks/checkout/selectors'
import { useCheckoutItemsFromOrders } from 'hooks/checkout'
import { fetchSchedules } from 'reducks/schedule/operations'
import { getSchedules } from 'reducks/schedule/selectors'
import { useIsAfterLastOrder, useNextBusinessDate } from 'hooks/store'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { useEarliestBusinessTime, useLatestOrderStartTime } from 'hooks/businessHour'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { BusinessTimeType } from 'types/store'
import { getLastOrderTime, getOrderStartTime, getStoreName } from 'reducks/store/selectors'
import { dateToString, parseTimeToDate } from 'lib/helper'
import format from 'date-fns/format'
import { isSameDay } from 'date-fns'
import { OrangeButton } from 'components/Button'
import { openBulkStockModal } from 'reducks/modal/operations'
const daysOfWeek = new DaysOfWeek()
const types = ['daily']

type Props = {
  page: 'released' | 'unreleased'
  stocks: StockType[]
}

const StockSettingSection: FC<Props> = (props) => {
  const { page, stocks } = props

  const dispatch = useDispatch()
  const breads = useSelector((state: State) => getBreads(state))
  const categories = useSelector((state: State) => getCategories(state))
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const schedules = useSelector((state: State) => getSchedules(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeName = useSelector((state: State) => getStoreName(state))
  const orders: OrderType[] = useSelector((state: State) => getOrders(state))

  const date = new Date()
  const checkoutItems = useCheckoutItemsFromOrders(orders)
  const nextBusinessDate = useNextBusinessDate(closedDates)
  const temporaryOpenCalendar = useGetTemporaryOpen(dateToString(date), storeCalendars)
  const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(date, specialHolidays)
  const latestOrderStartTime = useLatestOrderStartTime([dayOfWeek], orderStartTime, temporaryOpenCalendar)
  const earliestLastOrderTime = useEarliestBusinessTime(
    [dayOfWeek],
    lastOrderTime,
    'last_order_time',
    temporaryOpenCalendar
  )
  const lastOrderDateTime = parseTimeToDate(earliestLastOrderTime)
  const isAfterLastOrder = useIsAfterLastOrder(date, lastOrderDateTime)
  const isClosed = closedDates.some((closedDate) => isSameDay(closedDate, date))
  const strDate = format(isAfterLastOrder || isClosed ? nextBusinessDate : date, 'yyyyMMdd')
  const sortKey = 'order'

  const [categoryName, setCategoryName] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const [breadList, setBreadList] = useState<BreadType[]>([])

  // 注文された後に在庫データが削除されてしまった場合（有効在庫数がマイナスの場合）に在庫データを生成する
  const stockList = useAdjustStocks(
    breads,
    checkoutItems,
    strDate,
    latestOrderStartTime,
    earliestLastOrderTime,
    storeName,
    schedules,
    stocks,
    storeRef,
    types
  )

  // 選択されたカテゴリーに応じてフィルタリング
  const selectCategoryName = useSelectCategory(
    breads,
    categories,
    keyword,
    setCategoryName,
    setBreadList,
    page,
    stockList
  )
  // 検索キーワードの入力
  const handleKeyword = useHandleKeyword(breads, categories, categoryName, setKeyword, setBreadList, page, stockList)
  // キーワード検索(部分一致)
  const searchStocks = useSearchStocks(breads, categories, categoryName, keyword, setBreadList, page, stockList)
  useInitBreadList({ breads, categories, categoryName, keyword, setBreadList, page, stocks: stockList })

  // breadsを取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'public', sortKey, 'daily'))
  }, [storeRef, sortKey])

  // カテゴリーを取得
  useEffect(() => {
    dispatch(fetchEnabledCategories())
  }, [])

  // 受付設定を取得
  useEffect(() => {
    if (!storeRef) dispatch(fetchSchedules(storeRef, ['daily']))
  }, [storeRef])

  // カテゴリの選択肢を生成
  const categoryOptions = useCategoryOptions(categories)

  return (
    <section>
      <Spacer size={20} />
      {page === 'released' && (
        <OrangeButton
          label={'販売中の在庫を一括設定'}
          onClick={() => dispatch(openBulkStockModal(strDate))}
          size={'medium'}
          type={'main'}
        />
      )}
      <Spacer size={12} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchStocks(keyword)}
        placeholder={'商品名で検索する'}
        value={keyword}
      />
      <Spacer size={12} />
      <SelectBox
        dataCy={'stock-setting-section'}
        options={categoryOptions}
        select={selectCategoryName}
        value={categoryName}
      />
      <Spacer size={12} />
      <StockCardList breads={breadList} isEditable={true} page={page} stocks={stockList} />
    </section>
  )
}

export default StockSettingSection
